import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

interface PageSubtitleContextValue {
  subtitle: string | null
  setSubtitle: (s: string | null) => void
  onRename: ((newName: string) => Promise<void>) | null
  setOnRename: (cb: ((newName: string) => Promise<void>) | null) => void
  /** Page-specific controls rendered in the top bar (document history/comments/export…). */
  actions: ReactNode
  setActions: (node: ReactNode) => void
}

const PageSubtitleContext = createContext<PageSubtitleContextValue>({
  subtitle: null,
  setSubtitle: () => {},
  onRename: null,
  setOnRename: () => {},
  actions: null,
  setActions: () => {},
})

export function PageSubtitleProvider({ children }: { children: ReactNode }) {
  const [subtitle, setSubtitle] = useState<string | null>(null)
  const [onRename, setOnRename] = useState<((newName: string) => Promise<void>) | null>(null)
  const [actions, setActions] = useState<ReactNode>(null)
  const value = useMemo(
    () => ({
      subtitle,
      setSubtitle,
      onRename,
      setOnRename: (cb: typeof onRename) => setOnRename(() => cb),
      actions,
      setActions,
    }),
    [subtitle, onRename, actions]
  )
  return <PageSubtitleContext.Provider value={value}>{children}</PageSubtitleContext.Provider>
}

export function usePageSubtitle() {
  return useContext(PageSubtitleContext)
}

export function useSetPageSubtitle(
  subtitle: string | null,
  onRename?: (newName: string) => Promise<void>
) {
  const { setSubtitle, setOnRename } = useContext(PageSubtitleContext)
  useEffect(() => {
    setSubtitle(subtitle)
    return () => setSubtitle(null)
  }, [subtitle, setSubtitle])

  useEffect(() => {
    if (onRename) {
      setOnRename(onRename)
      return () => setOnRename(null)
    }
  }, [onRename, setOnRename])
}

/** Mount page actions in the top bar for the lifetime of the caller. Memoize `node`. */
export function useSetPageActions(node: ReactNode) {
  const { setActions } = useContext(PageSubtitleContext)
  useEffect(() => {
    setActions(node)
    return () => setActions(null)
  }, [node, setActions])
}
